/**
 * useTaxInvoices Hook
 * 세금계산서 관리 상태 및 액션
 */

import { useState, useCallback } from 'react';
import { TaxRepository } from '../repositories/TaxRepository';
import { TaxInvoice, CreateInvoiceRequest, UpdateInvoiceRequest, CsvUploadResult } from '../models/Tax';

const taxRepository = new TaxRepository();

export const useTaxInvoices = () => {
  const [invoices, setInvoices] = useState<TaxInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInvoices = useCallback(async (params: {
    year?: number;
    month?: number;
    direction?: string;
    search?: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      const data = await taxRepository.getInvoices(params);
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '세금계산서 조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  const createInvoice = useCallback(async (data: CreateInvoiceRequest): Promise<boolean> => {
    setError(null);
    try {
      await taxRepository.createInvoice(data);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : '세금계산서 추가 실패');
      return false;
    }
  }, []);

  const updateInvoice = useCallback(async (id: string, data: UpdateInvoiceRequest): Promise<boolean> => {
    setError(null);
    try {
      await taxRepository.updateInvoice(id, data);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : '세금계산서 수정 실패');
      return false;
    }
  }, []);

  const deleteInvoice = useCallback(async (id: string): Promise<boolean> => {
    setError(null);
    try {
      await taxRepository.deleteInvoice(id);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : '세금계산서 삭제 실패');
      return false;
    }
  }, []);

  const uploadCsv = useCallback(async (file: File): Promise<CsvUploadResult | null> => {
    setError(null);
    try {
      return await taxRepository.uploadInvoiceCsv(file);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'CSV 업로드 실패');
      return null;
    }
  }, []);

  const exportCsv = useCallback(async (year?: number, month?: number, direction?: string) => {
    try {
      const blob = await taxRepository.exportInvoicesCsv(year, month, direction);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NTD_세금계산서_${year || 'all'}_${month || 'all'}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'CSV 내보내기 실패');
    }
  }, []);

  const summary = {
    salesAmount: invoices.filter(i => i.direction === 'sales').reduce((sum, i) => sum + i.supply_amount, 0),
    salesTax: invoices.filter(i => i.direction === 'sales').reduce((sum, i) => sum + i.tax_amount, 0),
    purchaseAmount: invoices.filter(i => i.direction === 'purchase').reduce((sum, i) => sum + i.supply_amount, 0),
    purchaseTax: invoices.filter(i => i.direction === 'purchase').reduce((sum, i) => sum + i.tax_amount, 0),
    count: invoices.length,
  };

  return {
    invoices,
    loading,
    error,
    summary,
    fetchInvoices,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    uploadCsv,
    exportCsv,
  };
};
